import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Wordmark } from "@/components/site/Wordmark";

const LINKS = [
  { label: "Προϊόντα", href: "#products" },
  { label: "Ερωτήσεις", href: "#faq" },
  { label: "Ζητήστε Προσφορά", href: "#contact" },
];

export const Navbar = () => {
  const [solid, setSolid] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const onHome = pathname === "/";

  // Anchor links work in-page on the homepage; from other routes they need to
  // point back to "/" so the browser navigates home and then scrolls.
  const resolve = (href) =>
    href.startsWith("#") ? (onHome ? href : `/${href}`) : href;

  const logoHref = onHome ? "#hero" : "/";

  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      const hero = document.getElementById("hero");
      let trigger = window.innerHeight * 0.82;
      if (!onHome) {
        // Pages without the big dark hero (e.g. Calculator): switch to the
        // solid (dark-text on white) navbar as soon as we leave the short
        // dark header, so links never turn white over a white background.
        trigger = 80;
      } else if (hero) {
        trigger =
          hero.offsetHeight > window.innerHeight * 1.5
            ? hero.offsetHeight - window.innerHeight - 40
            : window.innerHeight * 0.82;
      }
      setSolid(y > trigger);

      // Hide on scroll down (past a small threshold), show on scroll up.
      // Always visible near the very top, and never hide while the mobile menu is open.
      const goingDown = y > lastY;
      if (open || y < 120) {
        setHidden(false);
      } else if (goingDown && y > 200) {
        setHidden(true);
      } else if (!goingDown) {
        setHidden(false);
      }
      lastY = y;
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [open, onHome]);

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-[90] transition-all duration-500 ${
        hidden ? "-translate-y-full" : "translate-y-0"
      } ${
        solid
          ? "border-b border-border/70 bg-white/85 backdrop-blur-xl py-3"
          : "border-b border-transparent py-5"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8">
        <a href={logoHref} data-testid="navbar-logo" className="flex items-center">
          <Wordmark light={!solid} className="h-12 sm:h-14" />
        </a>

        <nav className="hidden items-center gap-9 md:flex">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={resolve(l.href)}
              data-testid={`nav-link-${l.href.replace("#", "").replace("/", "")}`}
              className={`text-[17px] font-medium transition-colors hover:text-brand-500 ${
                solid ? "text-ink/70" : "text-white/80"
              }`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="/calculator"
            data-testid="navbar-cta"
            className={`hidden rounded-full px-5 py-2.5 text-base font-semibold transition-all duration-300 sm:inline-flex ${
              solid
                ? "bg-brand-500 text-white hover:bg-brand-600"
                : "bg-white text-ink hover:bg-brand-500 hover:text-white"
            }`}
          >
            Εκτίμηση Κόστους
          </a>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-trigger"
                aria-label="Μενού"
                className={`inline-flex h-10 w-10 items-center justify-center rounded-full md:hidden ${
                  solid ? "text-ink" : "text-white"
                }`}
              >
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[78%] border-l border-border bg-white">
              <SheetTitle className="sr-only">Μενού πλοήγησης</SheetTitle>
              <SheetDescription className="sr-only">
                Σύνδεσμοι πλοήγησης της VideoActive
              </SheetDescription>
              <div className="mt-10 flex flex-col gap-1">
                {LINKS.map((l) => (
                  <SheetClose asChild key={l.href}>
                    <a
                      href={resolve(l.href)}
                      data-testid={`mobile-nav-link-${l.href.replace("#", "").replace("/", "")}`}
                      className="border-b border-border py-4 font-display text-2xl font-semibold text-ink"
                    >
                      {l.label}
                    </a>
                  </SheetClose>
                ))}
                <SheetClose asChild>
                  <a
                    href="/calculator"
                    data-testid="mobile-nav-cta"
                    className="mt-6 inline-flex items-center justify-center rounded-full bg-brand-500 px-6 py-3.5 text-base font-semibold text-white"
                  >
                    Εκτίμηση Κόστους
                  </a>
                </SheetClose>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
